import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AnamVideoSimulation } from '@/components/simulation/AnamVideoSimulation';
import { toast } from 'sonner';
import { LoadingScreen } from '@/components/LoadingScreen';
import { useAuth } from '@/contexts/AuthContext';

const AnamSimulationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Generate sessionId ONCE on mount - stable across re-renders
  const sessionId = useMemo(() => {
    const id = crypto.randomUUID();
    return id;
  }, []);

  const { data: agent, isLoading, error } = useQuery({
    queryKey: ['simulation-agent', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('simulation_agents')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!id
  });

  // Validate agent configuration on load
  if (agent && agent.platform !== 'anam') {
    toast.error('This agent is not configured for Anam video conversations');
    navigate(`/simulation/${id}`);
  }

  if (agent && !agent.anam_persona_id) {
    toast.error('This agent is not configured with Anam persona ID');
    navigate(`/simulation/${id}`);
  }

  const handleSessionEnd = async (transcript: any[], sessionIdOrEvaluationId: string) => {
    // Session ID is passed, fetch evaluation by session_id (edge function creates it)
    navigate(`/simulation/results/${sessionIdOrEvaluationId}`);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Agent Not Found</h1>
          <p className="text-muted-foreground mb-4">
            The simulation agent could not be loaded or is not configured for video.
          </p>
          <button 
            onClick={() => navigate('/simulations')}
            className="text-primary hover:underline"
          >
            Return to Simulations
          </button>
        </div>
      </div>
    );
  }

  // Always use SDK mode
  return (
    <AnamVideoSimulation
      agentId={agent.id}
      sessionId={sessionId}
      userId={user?.id || ''}
      userName={user?.name || ''}
      userEmail={user?.email || ''}
      companyId={agent.company_id || user?.company_id || ''}
      onSessionEnd={handleSessionEnd}
    />
  );
};

export default AnamSimulationPage;
